'use client';

import { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

interface OrderResult {
  id: string;
  customer_name: string | null;
  customer_email: string | null;
  service_type: string | null;
  quantity: number | null;
  amount: number | null;
  payment_method: string | null;
  status: string | null;
  created_at: string | null;
}

const statusConfig: Record<string, { bg: string; color: string; border: string; label: string; icon: string }> = {
  pending:    { bg: 'rgba(244,169,35,0.10)', color: '#D97706', border: 'rgba(244,169,35,0.30)', label: 'En attente', icon: '⏳' },
  processing: { bg: 'rgba(59,130,246,0.10)', color: '#2563EB', border: 'rgba(59,130,246,0.30)', label: 'En cours de traitement', icon: '🔄' },
  completed:  { bg: 'rgba(16,185,129,0.10)', color: '#059669', border: 'rgba(16,185,129,0.30)', label: 'Livré', icon: '✅' },
  cancelled:  { bg: 'rgba(239,68,68,0.10)',  color: '#DC2626', border: 'rgba(239,68,68,0.30)',  label: 'Annulé', icon: '❌' },
};

export default function OrderTrackingPage() {
  const [email, setEmail] = useState('');
  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<OrderResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !orderId.trim()) return;

    setLoading(true);
    setError(null);
    setOrder(null);
    setSearched(false);

    try {
      const supabase = createClient();
      const { data, error: dbError } = await supabase
        .from('Orders')
        .select('id, customer_name, customer_email, service_type, quantity, amount, payment_method, status, created_at')
        .eq('id', orderId.trim())
        .eq('customer_email', email.trim().toLowerCase())
        .single();

      if (dbError || !data) {
        setError('Aucune commande trouvée pour cet email et cet identifiant. Vérifiez vos informations et réessayez.');
      } else {
        setOrder(data as OrderResult);
      }
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const handleReset = () => {
    setEmail('');
    setOrderId('');
    setOrder(null);
    setError(null);
    setSearched(false);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-CI', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusInfo = order?.status ? (statusConfig[order.status] ?? statusConfig['pending']) : null;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F4F6FB' }}>
      <Header />

      <main className="flex-1 pt-24 pb-16 px-4">
        <div className="max-w-xl mx-auto">

          {/* Page header */}
          <div className="text-center mb-10">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
              style={{ background: 'linear-gradient(135deg,#1877F2,#1565C0)', boxShadow: '0 8px 32px rgba(24,119,242,0.30)' }}
            >
              <Icon name="MagnifyingGlassIcon" size={28} className="text-white" />
            </div>
            <h1
              className="font-heading font-black text-3xl sm:text-4xl mb-3"
              style={{ color: '#0D1B3E', fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              Suivi de commande
            </h1>
            <p className="text-[#6B7280] text-base" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Entrez votre email et votre identifiant de commande pour consulter le statut de votre livraison.
            </p>
          </div>

          {/* Search form */}
          {!order && (
            <div
              className="bg-white rounded-3xl p-8 shadow-[0_4px_32px_rgba(13,27,62,0.08)] mb-6"
              style={{ border: '1px solid rgba(13,27,62,0.06)' }}
            >
              <form onSubmit={handleSearch} className="space-y-5">
                <div>
                  <label
                    htmlFor="tracking-email"
                    className="block text-sm font-bold text-[#374151] mb-2"
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                  >
                    Adresse email
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <Icon name="EnvelopeIcon" size={18} className="text-[#9CA3AF]" />
                    </div>
                    <input
                      id="tracking-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre@email.com"
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl text-sm text-[#0D1B3E] outline-none transition-all"
                      style={{
                        border: '1.5px solid rgba(13,27,62,0.12)',
                        background: '#FAFBFF',
                        fontFamily: 'DM Sans, sans-serif',
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="tracking-order-id"
                    className="block text-sm font-bold text-[#374151] mb-2"
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                  >
                    Identifiant de commande
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <Icon name="HashtagIcon" size={18} className="text-[#9CA3AF]" />
                    </div>
                    <input
                      id="tracking-order-id"
                      type="text"
                      required
                      value={orderId}
                      onChange={(e) => setOrderId(e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl text-sm text-[#0D1B3E] outline-none transition-all font-mono"
                      style={{
                        border: '1.5px solid rgba(13,27,62,0.12)',
                        background: '#FAFBFF',
                        fontFamily: 'monospace',
                      }}
                    />
                  </div>
                  <p className="text-xs text-[#9CA3AF] mt-1.5" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    💡 Retrouvez cet identifiant dans votre email de confirmation.
                  </p>
                </div>

                {/* Error message */}
                {error && searched && (
                  <div
                    className="flex items-start gap-3 p-4 rounded-xl"
                    style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.20)' }}
                  >
                    <span className="text-lg flex-shrink-0">❌</span>
                    <p className="text-sm text-[#DC2626]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      {error}
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email.trim() || !orderId.trim()}
                  className="w-full py-4 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 transition-all"
                  style={{
                    background: loading || !email.trim() || !orderId.trim()
                      ? 'rgba(13,27,62,0.20)'
                      : 'linear-gradient(135deg,#1877F2,#1565C0)',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    cursor: loading || !email.trim() || !orderId.trim() ? 'not-allowed' : 'pointer',
                    boxShadow: loading || !email.trim() || !orderId.trim() ? 'none' : '0 4px 20px rgba(24,119,242,0.30)',
                  }}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Recherche en cours...
                    </>
                  ) : (
                    <>
                      <Icon name="MagnifyingGlassIcon" size={16} className="text-white" />
                      Rechercher ma commande
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Order result */}
          {order && statusInfo && (
            <div className="space-y-4">
              {/* Status banner */}
              <div
                className="rounded-2xl p-5 flex items-center gap-4"
                style={{ background: statusInfo.bg, border: `1.5px solid ${statusInfo.border}` }}
              >
                <span className="text-3xl">{statusInfo.icon}</span>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider mb-0.5" style={{ color: statusInfo.color, fontFamily: 'DM Sans, sans-serif' }}>
                    Statut de la commande
                  </div>
                  <div className="font-heading font-black text-lg" style={{ color: statusInfo.color, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {statusInfo.label}
                  </div>
                </div>
              </div>

              {/* Order details card */}
              <div
                className="bg-white rounded-3xl overflow-hidden shadow-[0_4px_32px_rgba(13,27,62,0.08)]"
                style={{ border: '1px solid rgba(13,27,62,0.06)' }}
              >
                {/* Card header */}
                <div
                  className="px-6 py-4 flex items-center justify-between"
                  style={{ background: 'linear-gradient(135deg,#0D1B3E,#162550)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                >
                  <div>
                    <div className="text-white/60 text-xs font-medium mb-0.5" style={{ fontFamily: 'DM Sans, sans-serif' }}>Commande</div>
                    <div className="text-white font-mono text-xs truncate max-w-[220px]">{order.id}</div>
                  </div>
                  <span className="text-2xl">📦</span>
                </div>

                {/* Details rows */}
                <div className="divide-y" style={{ borderColor: 'rgba(13,27,62,0.06)' }}>
                  {[
                    { label: 'Service', value: order.service_type || '—', icon: '🎯' },
                    { label: 'Quantité', value: order.quantity ? order.quantity.toLocaleString('fr-CI') : '—', icon: '📊' },
                    { label: 'Montant', value: order.amount ? `${Number(order.amount).toLocaleString('fr-CI')} XOF` : '—', icon: '💰', highlight: true },
                    { label: 'Méthode de paiement', value: order.payment_method || '—', icon: '💳' },
                    { label: 'Date de commande', value: formatDate(order.created_at), icon: '📅' },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between px-6 py-4">
                      <div className="flex items-center gap-2.5">
                        <span className="text-base">{row.icon}</span>
                        <span className="text-sm text-[#6B7280]" style={{ fontFamily: 'DM Sans, sans-serif' }}>{row.label}</span>
                      </div>
                      <span
                        className="text-sm font-bold text-right"
                        style={{
                          color: row.highlight ? '#059669' : '#0D1B3E',
                          fontFamily: 'Plus Jakarta Sans, sans-serif',
                          fontSize: row.highlight ? 15 : 13,
                        }}
                      >
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={handleReset}
                  className="flex-1 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2"
                  style={{
                    border: '1.5px solid rgba(13,27,62,0.15)',
                    color: '#0D1B3E',
                    background: 'white',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                  }}
                >
                  <Icon name="ArrowPathIcon" size={15} className="text-[#0D1B3E]" />
                  Nouvelle recherche
                </button>
                <Link
                  href="/services"
                  className="flex-1 py-3 rounded-xl text-sm font-bold text-white text-center flex items-center justify-center gap-2 transition-all"
                  style={{
                    background: 'linear-gradient(135deg,#1877F2,#1565C0)',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    boxShadow: '0 4px 16px rgba(24,119,242,0.25)',
                    textDecoration: 'none',
                  }}
                >
                  🚀 Commander à nouveau
                </Link>
              </div>

              {/* Support note */}
              <div
                className="p-4 rounded-2xl text-center"
                style={{ background: 'rgba(13,27,62,0.04)', border: '1px solid rgba(13,27,62,0.06)' }}
              >
                <p className="text-xs text-[#6B7280]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  💬 Une question ? Contactez-nous via WhatsApp :{' '}
                  <a href="https://wa.me/2250700000000" className="text-[#1877F2] font-bold hover:underline">
                    +225 07 00 00 00 00
                  </a>
                </p>
              </div>
            </div>
          )}

          {/* Help text */}
          {!order && !searched && (
            <div
              className="p-5 rounded-2xl text-center"
              style={{ background: 'rgba(24,119,242,0.05)', border: '1px solid rgba(24,119,242,0.12)' }}
            >
              <p className="text-xs text-[#374151]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                📧 Votre identifiant de commande se trouve dans l'email de confirmation envoyé après votre achat.
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
